import { AGORA_SAMPLE_INTERVAL_MS, AgoraSample } from './agoraSampler';

import { AGORA } from '@/constants';

export type NumRollup = {
  avg: number | null;
  min: number | null;
  max: number | null;
  p95: number | null;
  last: number | null;
};

export function numRollup(values: (number | null | undefined)[]): NumRollup {
  const nums = values.filter((v): v is number => v != null && !isNaN(v as number));
  if (nums.length === 0) return { avg: null, min: null, max: null, p95: null, last: null };
  const sorted = [...nums].sort((a, b) => a - b);
  return {
    avg: nums.reduce((a, b) => a + b, 0) / nums.length,
    min: sorted[0],
    max: sorted[sorted.length - 1],
    p95: sorted[Math.min(Math.floor(sorted.length * 0.95), sorted.length - 1)],
    last: nums[nums.length - 1]
  };
}

export function windowElapsedMs(samples: Pick<AgoraSample, 'ts'>[]): number {
  return samples.length > 1 ? samples[samples.length - 1].ts - samples[0].ts : AGORA_SAMPLE_INTERVAL_MS;
}

export function rf(prefix: string, values: (number | null | undefined)[]): Record<string, number | null> {
  const { avg, min, max, p95, last } = numRollup(values);
  return {
    [`${prefix}_avg`]: avg,
    [`${prefix}_min`]: min,
    [`${prefix}_max`]: max,
    [`${prefix}_p95`]: p95,
    [`${prefix}_last`]: last
  };
}

export type AgoraRollupRoomInfo = {
  observerEventUserId: string | null;
  clientUid: string;
  clientConnectionState: string | null;
  screenUid: string;
  screenConnectionState: string | null;
  publishesCamera?: boolean;
};

export type AgoraParticipantRecord = Record<string, unknown>;

const BOT_UID_MARKERS: string[] = [
  AGORA.UID_SUFFIX.TV_SCREEN,
  AGORA.UID_SUFFIX.LIVE_STREAMER,
  AGORA.UID_SUFFIX.RECORDER
];
const SCREEN_UID_MARKER: string = AGORA.UID_SUFFIX.SCREEN_STREAM;

export function buildAgoraParticipantRecords(
  samples: AgoraSample[],
  commonFields: Record<string, unknown>,
  room: AgoraRollupRoomInfo
): AgoraParticipantRecord[] {
  if (samples.length === 0) return [];
  const records: AgoraParticipantRecord[] = [];
  const lastSample = samples[samples.length - 1];

  if (room.publishesCamera !== false) {
    records.push(buildLocalCameraRecord(samples, lastSample, commonFields, room));
  }

  const screenSamples = samples.filter(
    (s): s is AgoraSample & { screenVideo: Record<string, any> } => s.screenVideo != null
  );
  if (screenSamples.length > 0) {
    const lastScreen = screenSamples[screenSamples.length - 1].screenVideo;
    records.push({
      ...commonFields,
      sample_count: screenSamples.length,
      elapsed_ms: windowElapsedMs(screenSamples),
      observed_event_user_id: room.observerEventUserId,
      agora_uid: room.screenUid,
      uid_mapping_status: 'self',
      direction: 'local_upstream',
      stream_kind: 'screen',
      connection_state: room.screenConnectionState,
      ...rf(
        'video_send_bitrate',
        screenSamples.map(s => s.screenVideo.sendBitrate)
      ),
      ...rf(
        'video_send_fps',
        screenSamples.map(s => s.screenVideo.sendFrameRate)
      ),
      ...rf(
        'video_send_jitter_ms',
        screenSamples.map(s => s.screenVideo.sendJitterMs)
      ),
      ...rf(
        'video_send_resolution_height',
        screenSamples.map(s => s.screenVideo.sendResolutionHeight)
      ),
      ...rf(
        'video_send_resolution_width',
        screenSamples.map(s => s.screenVideo.sendResolutionWidth)
      ),
      ...rf(
        'video_captured_fps',
        screenSamples.map(s => s.screenVideo.captureFrameRate)
      ),
      video_target_send_bitrate: lastScreen.targetSendBitrate ?? null,
      video_total_freeze_time_ms: lastScreen.totalFreezeTime ?? null
    });
  }

  const allUids = new Set<string>();
  for (const s of samples) {
    if (s.remoteVideo) for (const uid of Object.keys(s.remoteVideo)) allUids.add(uid);
  }
  for (const uid of allUids) {
    const presentSamples = samples.filter(s => s.remoteVideo?.[uid] || s.remoteAudio?.[uid]);
    const rvSamples = samples.map(s => s.remoteVideo?.[uid]).filter(Boolean) as Record<string, any>[];
    const raSamples = samples.map(s => s.remoteAudio?.[uid]).filter(Boolean) as Record<string, any>[];
    const isBotUid = BOT_UID_MARKERS.some(marker => uid.includes(marker));
    const hasPrefix = uid.includes('|') && !isBotUid;
    const streamKind = uid.includes(SCREEN_UID_MARKER)
      ? 'screen'
      : isBotUid
        ? uid.slice(uid.lastIndexOf('|') + 1)
        : 'camera';

    records.push({
      ...commonFields,
      sample_count: Math.max(rvSamples.length, raSamples.length),
      elapsed_ms: windowElapsedMs(presentSamples),
      observed_event_user_id: hasPrefix ? uid.split('|')[0] : null,
      agora_uid: uid,
      uid_mapping_status: isBotUid ? 'bot' : hasPrefix ? 'mapped' : 'unmapped',
      direction: 'remote_downstream',
      stream_kind: streamKind,
      ...rf(
        'video_receive_bitrate',
        rvSamples.map(s => s.receiveBitrate)
      ),
      ...rf(
        'video_receive_fps',
        rvSamples.map(s => s.receiveFrameRate)
      ),
      ...rf(
        'video_packet_loss_rate',
        rvSamples.map(s => s.packetLossRate)
      ),
      ...rf(
        'video_end2end_delay_ms',
        rvSamples.map(s => s.end2EndDelay)
      ),
      ...rf(
        'video_freeze_rate',
        rvSamples.map(s => s.freezeRate)
      ),
      ...rf(
        'audio_receive_packet_loss_rate',
        raSamples.map(s => s.packetLossRate)
      ),
      ...rf(
        'audio_current_packet_loss_rate',
        raSamples.map(s => s.currentPacketLossRate)
      ),
      ...rf(
        'audio_end2end_delay_ms',
        raSamples.map(s => s.end2EndDelay)
      ),
      ...rf(
        'audio_receive_delay_ms',
        raSamples.map(s => s.receiveDelay)
      ),
      ...rf(
        'audio_receive_level',
        raSamples.map(s => s.receiveLevel)
      ),
      ...rf(
        'audio_freeze_rate',
        raSamples.map(s => s.freezeRate)
      )
    });
  }

  return records;
}

function buildLocalCameraRecord(
  samples: AgoraSample[],
  lastSample: AgoraSample,
  commonFields: Record<string, unknown>,
  room: AgoraRollupRoomInfo
): AgoraParticipantRecord {
  return {
    ...commonFields,
    sample_count: samples.length,
    observed_event_user_id: room.observerEventUserId,
    agora_uid: room.clientUid,
    uid_mapping_status: 'self',
    direction: 'local_upstream',
    stream_kind: 'camera',
    connection_state: room.clientConnectionState,
    ...rf(
      'audio_send_bitrate',
      samples.map(s => s.localAudio?.sendBitrate)
    ),
    ...rf(
      'audio_send_jitter_ms',
      samples.map(s => s.localAudio?.sendJitterMs)
    ),
    ...rf(
      'audio_send_rtt_ms',
      samples.map(s => s.localAudio?.sendRttMs)
    ),
    ...rf(
      'audio_current_packet_loss_rate',
      samples.map(s => s.localAudio?.currentPacketLossRate)
    ),
    ...rf(
      'audio_send_level',
      samples.map(s => s.localAudio?.sendVolumeLevel)
    ),
    audio_send_packets_lost: lastSample.localAudio?.sendPacketsLost ?? null,
    ...rf(
      'video_send_bitrate',
      samples.map(s => s.localVideo?.sendBitrate)
    ),
    ...rf(
      'video_send_fps',
      samples.map(s => s.localVideo?.sendFrameRate)
    ),
    ...rf(
      'video_send_jitter_ms',
      samples.map(s => s.localVideo?.sendJitterMs)
    ),
    ...rf(
      'video_send_resolution_height',
      samples.map(s => s.localVideo?.sendResolutionHeight)
    ),
    ...rf(
      'video_send_resolution_width',
      samples.map(s => s.localVideo?.sendResolutionWidth)
    ),
    ...rf(
      'video_captured_resolution_height',
      samples.map(s => s.localVideo?.captureResolutionHeight)
    ),
    ...rf(
      'video_captured_resolution_width',
      samples.map(s => s.localVideo?.captureResolutionWidth)
    ),
    ...rf(
      'video_captured_fps',
      samples.map(s => s.localVideo?.captureFrameRate)
    ),
    ...rf(
      'video_encode_delay_ms',
      samples.map(s => s.localVideo?.encodeDelay)
    ),
    video_target_send_bitrate: lastSample.localVideo?.targetSendBitrate ?? null,
    video_total_freeze_time_ms: lastSample.localVideo?.totalFreezeTime ?? null
  };
}
